import { supabase } from './supabase'
import { getSessionHeaders } from './serverSession'

// API helper for notification operations - NO getServiceRoleClient() in browser
async function callNotificationAPI(userId: number, operation: string, data: Record<string, any> = {}): Promise<any> {
 // SECURITY v2.9.14: Include session headers for server-side userId resolution
 const response = await fetch('/api/user/notifications', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ userId, operation, data })
 });

 if (!response.ok) {
 throw new Error(`Notification API error: ${response.status}`);
 }

 return response.json();
}

export interface NotificationData {
 user_id: number
 type: 'payment_approved' | 'payment_rejected' | 'payment_reversed' | 'commission_earned' | 'system' | 'referral' | 'withdrawal' | 'share_purchase' | 'phase_change' | 'account_update' | 'security_alert' | 'info' | 'success' | 'warning' | 'error' | 'payment' | 'commission' | 'referral_signup' | 'share_transfer' | 'certificate'
 title: string
 message: string
 metadata?: Record<string, any>
 action_url?: string
}

export interface NotificationTemplate {
 id: string
 template_key: string
 notification_type: string
 title_template: string
 message_template: string
 variables: string[]
 is_active: boolean
}

export interface UserNotification {
 id: string
 user_id: number
 type: string
 title: string
 message: string
 metadata: Record<string, any>
 is_read: boolean
 is_archived: boolean
 action_url?: string
 priority?: 'low' | 'normal' | 'high' | 'urgent'
 created_at: string
 updated_at: string
}

class NotificationService {
 /**
 * Create a new notification for a user
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 async createNotification(notificationData: NotificationData): Promise<UserNotification | null> {
 try {
 const result = await callNotificationAPI(notificationData.user_id, 'createNotification', {
 notificationData
 });

 if (!result.success || !result.data?.notification) {
 console.error('Error creating notification:', result.error)
 return null
 }

 console.log(`Notification created for user ${notificationData.user_id}:`, result.data.notification.title)
 return result.data.notification
 } catch (error) {
 console.error('Error in createNotification:', error)
 return null
 }
 }

 /**
 * Create notification from template with variable substitution
 */
 async createNotificationFromTemplate(
 templateKey: string,
 userId: number,
 variables: Record<string, any>,
 _options?: {
 priority?: 'low' | 'normal' | 'high' | 'urgent'
 payment_id?: string
 commission_id?: string
 referral_id?: string
 }
 ): Promise<UserNotification | null> {
 try {
 // Get the template
 const { data: template, error: templateError } = await supabase
 .from('notification_templates')
 .select('*')
 .eq('template_key', templateKey)
 .eq('is_active', true)
 .single()

 if (templateError || !template) {
 console.error(`Template not found: ${templateKey}`, templateError)
 return null
 }

 // Substitute variables in title and message
 const title = this.substituteVariables(template.title_template, variables)
 const message = this.substituteVariables(template.message_template, variables)

 // Create the notification
 return await this.createNotification({
 user_id: userId,
 type: template.notification_type as any,
 title,
 message,
 metadata: { template_key: templateKey, variables, ...variables }
 })
 } catch (error) {
 console.error('Error creating notification from template:', error)
 return null
 }
 }

 /**
 * Send payment approval notification
 */
 async sendPaymentApprovalNotification(
 userId: number,
 paymentData: {
 payment_id: string
 amount: number
 shares: number
 price_per_share: number
 network: string
 processed_date: string
 }
 ): Promise<UserNotification | null> {
 return await this.createNotificationFromTemplate(
 'payment_approved',
 userId,
 {
 amount: paymentData.amount.toFixed(2),
 shares: paymentData.shares.toLocaleString(),
 price_per_share: paymentData.price_per_share.toFixed(2),
 payment_id: paymentData.payment_id,
 processed_date: new Date(paymentData.processed_date).toLocaleString()
 },
 {
 priority: 'high',
 payment_id: paymentData.payment_id
 }
 )
 }

 /**
 * Send payment rejection notification
 */
 async sendPaymentRejectionNotification(
 userId: number,
 paymentData: {
 payment_id: string
 amount: number
 network: string
 tx_hash: string
 rejection_reason: string
 rejected_date: string
 }
 ): Promise<UserNotification | null> {
 return await this.createNotificationFromTemplate(
 'payment_rejected',
 userId,
 {
 amount: paymentData.amount.toFixed(2),
 network: paymentData.network,
 tx_hash: paymentData.tx_hash || 'Not provided',
 rejection_reason: paymentData.rejection_reason,
 payment_id: paymentData.payment_id,
 rejected_date: new Date(paymentData.rejected_date).toLocaleString()
 },
 {
 priority: 'high',
 payment_id: paymentData.payment_id
 }
 )
 }

 /**
 * Send payment reversal notification
 */
 async sendPaymentReversalNotification(
 userId: number,
 paymentData: {
 payment_id: string
 amount: number
 network: string
 tx_hash: string
 reversal_reason: string
 reversed_date: string
 shares_removed: number
 }
 ): Promise<UserNotification | null> {
 // Create direct notification since template system might not be fully set up
 return await this.createNotification({
 user_id: userId,
 type: 'payment_reversed',
 title: ` Payment Reversed - $${paymentData.amount.toFixed(2)} USDT`,
 message: `Your payment of $${paymentData.amount.toFixed(2)} USDT has been reversed by our admin team.\n\n Reversal Details:\n• Amount: $${paymentData.amount.toFixed(2)} USDT\n• Network: ${paymentData.network}\n• Transaction Hash: ${paymentData.tx_hash || 'Not provided'}\n• Shares Removed: ${paymentData.shares_removed.toLocaleString()} shares\n• Reason: ${paymentData.reversal_reason}\n• Reversed Date: ${new Date(paymentData.reversed_date).toLocaleString()}\n\n What This Means:\n• Your ${paymentData.shares_removed.toLocaleString()} shares have been removed from your account\n• Any commissions related to this payment have been reversed\n• This action is permanent and cannot be undone\n\nIf you have questions about this reversal, please contact our support team.`,
 metadata: {
 payment_id: paymentData.payment_id,
 amount: paymentData.amount,
 network: paymentData.network,
 tx_hash: paymentData.tx_hash,
 reversal_reason: paymentData.reversal_reason,
 shares_removed: paymentData.shares_removed,
 reversed_date: paymentData.reversed_date
 },
 action_url: '/dashboard?section=payments'
 })
 }

 /**
 * Send commission earned notification
 */
 async sendCommissionEarnedNotification(
 referrerId: number,
 commissionData: {
 commission_id: string
 referred_username: string
 investment_amount: number
 shares_purchased: number
 usdt_commission: number
 share_commission: number
 total_usdt_balance: number
 total_share_balance: number
 earned_date: string
 }
 ): Promise<UserNotification | null> {
 const totalCommission = commissionData.usdt_commission + (commissionData.share_commission * 5) // Assuming $5 per share value

 // Add safety checks for undefined values
 const safeCommissionData = {
 share_commission: commissionData.share_commission || 0,
 referred_username: commissionData.referred_username || 'Unknown',
 investment_amount: commissionData.investment_amount || 0,
 shares_purchased: commissionData.shares_purchased || 0,
 usdt_commission: commissionData.usdt_commission || 0,
 total_usdt_balance: commissionData.total_usdt_balance || 0,
 total_share_balance: commissionData.total_share_balance || 0,
 commission_id: commissionData.commission_id || 'unknown',
 earned_date: commissionData.earned_date || new Date()
 };

 return await this.createNotificationFromTemplate(
 'commission_earned',
 referrerId,
 {
 total_commission: totalCommission.toFixed(2),
 share_commission: safeCommissionData.share_commission.toFixed(2),
 referred_username: safeCommissionData.referred_username,
 investment_amount: safeCommissionData.investment_amount.toFixed(2),
 shares_purchased: safeCommissionData.shares_purchased.toLocaleString(),
 usdt_commission: safeCommissionData.usdt_commission.toFixed(2),
 total_usdt_balance: safeCommissionData.total_usdt_balance.toFixed(2),
 total_share_balance: safeCommissionData.total_share_balance.toFixed(2),
 commission_id: safeCommissionData.commission_id,
 earned_date: new Date(safeCommissionData.earned_date).toLocaleString()
 },
 {
 priority: 'normal',
 commission_id: safeCommissionData.commission_id
 }
 )
 }

 /**
 * Get user notifications with pagination and filtering
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
 async getUserNotifications(
 userId: number,
 options?: {
 limit?: number
 offset?: number
 type?: string
 unread_only?: boolean
 include_archived?: boolean
 }
 ): Promise<{ notifications: UserNotification[], total_count: number }> {
 try {
 const result = await callNotificationAPI(userId, 'getUserNotifications', { options });

 if (!result.success) {
 console.error('Error fetching user notifications:', result.error)
 return { notifications: [], total_count: 0 }
 }

 return {
 notifications: result.data?.notifications || [],
 total_count: result.data?.total_count || 0
 }
 } catch (error) {
 console.error('Error in getUserNotifications:', error)
 return { notifications: [], total_count: 0 }
 }
 }

 /**
 * Mark notification as read
 */
 async markAsRead(notificationId: string, userId: number): Promise<boolean> {
 try {
 const { error } = await supabase
 .from('user_notifications')
 .update({
 is_read: true,
 updated_at: new Date().toISOString()
 })
 .eq('id', notificationId)
 .eq('user_id', userId) // Security: ensure user can only mark their own notifications

 if (error) {
 console.error('Error marking notification as read:', error)
 return false
 }

 return true
 } catch (error) {
 console.error('Error in markAsRead:', error)
 return false
 }
 }

 /**
 * Mark all notifications as read for a user
 */
 async markAllAsRead(userId: number): Promise<boolean> {
 try {
 const { error } = await supabase
 .from('user_notifications')
 .update({
 is_read: true,
 updated_at: new Date().toISOString()
 })
 .eq('user_id', userId)
 .eq('is_read', false)

 if (error) {
 console.error('Error marking all notifications as read:', error)
 return false
 }

 return true
 } catch (error) {
 console.error('Error in markAllAsRead:', error)
 return false
 }
 }

 /**
 * Archive notification
 */
 async archiveNotification(notificationId: string, userId: number): Promise<boolean> {
 try {
 const { error } = await supabase
 .from('user_notifications')
 .update({
 is_archived: true,
 updated_at: new Date().toISOString()
 })
 .eq('id', notificationId)
 .eq('user_id', userId)

 if (error) {
 console.error('Error archiving notification:', error)
 return false
 }

 return true
 } catch (error) {
 console.error('Error in archiveNotification:', error)
 return false
 }
 }

 /**
 * Get notification statistics for a user via API
 */
 async getNotificationStats(userId: number): Promise<{
 total: number
 unread: number
 by_type: Record<string, number>
 }> {
 try {
 console.log('Getting notification stats for user:', userId)

 // Use the existing working API endpoint
 // SECURITY v2.9.14: Include session headers; server derives userId from session
 const response = await fetch(`/api/user-notifications?type=summary`, {
  headers: { ...getSessionHeaders() }
 })

 if (!response.ok) {
 console.warn('Notification stats API failed, returning defaults')
 return { total: 0, unread: 0, by_type: {} }
 }

 const result = await response.json()

 if (!result.success || !result.data) {
 return { total: 0, unread: 0, by_type: {} }
 }

 const data = result.data
 return {
 total: data.total_notifications || 0,
 unread: data.unread_count || 0,
 by_type: {
 payment_approved: data.payment_approved_count || 0,
 payment_rejected: data.payment_rejected_count || 0,
 commission_earned: data.commission_count || 0
 }
 }
 } catch (error) {
 console.error('Error getting notification stats:', error)
 return { total: 0, unread: 0, by_type: {} }
 }
 }

 /**
 * Private method to substitute variables in templates
 */
 private substituteVariables(template: string, variables: Record<string, any>): string {
 let result = template

 // Replace {{variable}} patterns
 Object.keys(variables).forEach(key => {
 const regex = new RegExp(`{{${key}}}`, 'g')
 result = result.replace(regex, String(variables[key] || ''))
 })

 return result
 }
}

// Export singleton instance
export const notificationService = new NotificationService()
export default notificationService
